/**
 * Whether a page is visible to the signed-in user.
 *
 * There are two independent reasons a page can be hidden, and a caller that
 * checks only one gets the wrong answer:
 *
 *   1. Permissions and tenant capabilities, which nav.visibleNav applies.
 *   2. A per-user layout customization saved by a superadmin, which lives at
 *      /auth/customizations/app/nav and can hide a page the user is otherwise
 *      entitled to see.
 *
 * The shell filters the sidebar by both. Anything else asking "may this person
 * see that page" has to ask the same question, or it will show data for a page
 * the user cannot open - which is exactly what the reports page did with alert
 * figures before this existed.
 *
 * @module app/page-visibility
 */
import { apiFetch } from "../auth.ts";
import { navItemVisible } from "./nav.ts";

/**
 * The saved customization, fetched once per session and shared.
 *
 * A promise rather than a value so concurrent callers await the same request
 * instead of racing to issue their own, and so a caller that arrives before it
 * resolves waits rather than reading an empty map and deciding "visible".
 */
let overrides: Promise<Map<string, boolean>> | null = null;

/** Drop the cached customization. Call when the signed-in user changes. */
export function resetNavOverrides(): void {
	overrides = null;
}

/** Page id to whether the customization shows it. Absent means not customized. */
export function navOverrides(): Promise<Map<string, boolean>> {
	if (!overrides) {
		overrides = apiFetch("/auth/customizations/app/nav")
			.then((res) => (res?.ok ? res.json() : null))
			.then((data) => {
				const map = new Map<string, boolean>();
				if (data && Array.isArray(data.widgets)) {
					for (const w of data.widgets) {
						if (w && typeof w.id === "string") {
							map.set(w.id, w.visible !== false);
						}
					}
				}
				return map;
			})
			.catch(() => {
				// No customization is the common case and not an error. Failing open
				// matches the sidebar, which shows the un-customized nav when this
				// call fails rather than hiding the whole application.
				return new Map<string, boolean>();
			});
	}
	return overrides;
}

/**
 * Both gates: entitlement first, then the per-user customization.
 *
 * Entitlement is checked first because it is synchronous and decisive - a page
 * the user has no permission for stays hidden whatever the customization says.
 */
export async function pageVisible(
	id: string,
	role: string,
	permissions: string[] = [],
	capabilities: string[] = [],
): Promise<boolean> {
	if (!navItemVisible(id, role, permissions, capabilities)) return false;
	return (await navOverrides()).get(id) !== false;
}
